import React, { useState, useEffect } from 'react';
import DashboardCard from '@/components/ui/DashboardCard';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { ImageDetail } from '@/components/image-lightbox';
import { motion } from 'framer-motion';
import { Images, ArrowRight, ArrowUp } from 'lucide-react';
import GlassPill from '@/components/ui/GlassPill';

// Import private image generator hooks and types
import { usePrivateImageLibraryData } from '@/hooks/private-image-generator/usePrivateImageLibrary';
import type { PrivateImageGeneratorItem, PrivateImageGeneratorFilter } from '@/hooks/private-image-generator/types';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { 
  useStaticStylePrompts, 
  useStaticInfluencePrompts, 
  useStaticColorPrompts, 
  useStaticBackgroundPrompts, 
  useStaticEnvironmentPrompts,
  useStaticMoodPrompts
} from '@/hooks/image-generator/static-filters/hooks';

// Import shared components from the image generator
import { ImageGrid } from '@/components/image-generator/ImageLibrary/components/ImageGrid';
import { LoadMoreIndicator } from '@/components/image-generator/ImageLibrary/components/LoadMoreIndicator';
import { ErrorState } from '@/components/image-generator/ImageLibrary/components/ErrorState';

// Import the dedicated PrivateImageGeneratorSidebar for custom lightbox sidebar
import { PrivateImageGeneratorSidebar } from './PrivateImageGeneratorSidebar';

interface PrivateImageLibraryProps {
  styleId?: string;
  influenceId?: string;
  backgroundId?: string;
  environmentIds?: string[];
  moodIds?: string[];
  colorIds?: string[];
  imageType?: string;
  description?: string;
  debouncedDescription?: string;
  width?: string;
  height?: string;
  selectedImage?: PrivateImageGeneratorItem | null;
  onImageSelect?: (image: PrivateImageGeneratorItem | null) => void;
  projectId?: string | null;
  autoOpenImageId?: string | null;
  onAutoOpenComplete?: () => void;
}

// Transform private image data to match ImageGrid expectations
const transformPrivateImageToLibraryItem = (privateImage: PrivateImageGeneratorItem): any => ({
  id: privateImage.id,
  image_url: privateImage.image_url,
  width: privateImage.width || 512,
  height: privateImage.height || 512,
  description: privateImage.generation_prompt || privateImage.alt_text,
  user_id: privateImage.user_id,
  favorite_count: 0, // Private images don't have favorites
  storage_path: privateImage.storage_path,
});

export const PrivateImageLibrary: React.FC<PrivateImageLibraryProps> = ({
  selectedImage: propSelectedImage,
  onImageSelect: propOnImageSelect,
  debouncedDescription,
  autoOpenImageId,
  onAutoOpenComplete,
  ...filterProps
}) => {
  // Internal state for uncontrolled mode
  const [internalSelectedImage, setInternalSelectedImage] = useState<PrivateImageGeneratorItem | null>(null);
  const [description, setDescription] = useState(filterProps.description || '');

  // Controlled vs uncontrolled pattern - single source of truth
  const selectedImage = propSelectedImage !== undefined ? propSelectedImage : internalSelectedImage;
  const setSelectedImage = propOnImageSelect || setInternalSelectedImage;

  // Update description when props change
  useEffect(() => {
    if (filterProps.description !== undefined) {
      setDescription(filterProps.description);
    }
  }, [filterProps.description]);

  // Get project data to show project name in header
  const { data: workbookData } = useWorkbookQuery({ 
    filterByStatus: 'all' 
  });
  const projects = workbookData?.projects || [];
  
  // Find selected project for display
  const selectedProject = filterProps.projectId 
    ? projects.find(project => project.id === filterProps.projectId)
    : null;

  // Get static filter data for label mapping only - doesn't interfere with parent state
  const { data: stylePrompts } = useStaticStylePrompts(filterProps.imageType || 'Photo');
  const { data: influencePrompts } = useStaticInfluencePrompts(filterProps.imageType || 'Photo');
  const { data: colorPrompts } = useStaticColorPrompts(filterProps.imageType || 'Photo');
  const { data: backgroundPrompts } = useStaticBackgroundPrompts(filterProps.imageType || 'Photo');
  const { data: environmentPrompts } = useStaticEnvironmentPrompts(filterProps.imageType || 'Photo');
  const { data: moodPrompts } = useStaticMoodPrompts(filterProps.imageType || 'Photo');

  // Helper functions to map IDs to labels using prompt data
  const getFilterLabel = (id: string | undefined, prompts: any[]) => {
    if (!id) return null;
    if (!prompts || prompts.length === 0) {
      // If prompts aren't loaded yet, return the ID as-is to show it's working
      return id;
    }
    const prompt = prompts.find(p => p.id === id);
    return prompt?.prompt_name || id;
  };

  const getActiveFilters = () => {
    const filters = [];
    
    if (searchTerm) {
      filters.push(`"${searchTerm}"`);
    }
    
    // Don't show project name since it's already displayed in the title
    // if (selectedProject) {
    //   filters.push(selectedProject.title);
    // }
    
    if (filterProps.imageType) {
      filters.push(filterProps.imageType);
    }
    
    const styleLabel = getFilterLabel(filterProps.styleId, stylePrompts);
    if (styleLabel) {
      filters.push(styleLabel);
    }
    
    const influenceLabel = getFilterLabel(filterProps.influenceId, influencePrompts);
    if (influenceLabel) {
      filters.push(influenceLabel);
    }
    
    const backgroundLabel = getFilterLabel(filterProps.backgroundId, backgroundPrompts);
    if (backgroundLabel) {
      filters.push(backgroundLabel);
    }
    
    // For multi-select filters, get labels for checked items
    if (filterProps.environmentIds && filterProps.environmentIds.length > 0) {
      const envLabels = filterProps.environmentIds
        .map(id => getFilterLabel(id, environmentPrompts))
        .filter(Boolean);
      filters.push(...envLabels);
    }
    
    if (filterProps.moodIds && filterProps.moodIds.length > 0) {
      const moodLabels = filterProps.moodIds
        .map(id => getFilterLabel(id, moodPrompts))
        .filter(Boolean);
      filters.push(...moodLabels);
    }
    
    if (filterProps.colorIds && filterProps.colorIds.length > 0) {
      const colorLabels = filterProps.colorIds
        .map(id => getFilterLabel(id, colorPrompts))
        .filter(Boolean);
      filters.push(...colorLabels);
    }
    
    if (filterProps.width && filterProps.height) {
      filters.push(`${filterProps.width}×${filterProps.height}`);
    }
    
    return filters;
  };

  // Build filters object for private image data
  const filters: PrivateImageGeneratorFilter = {
    styleId: filterProps.styleId,
    influenceId: filterProps.influenceId,
    backgroundId: filterProps.backgroundId,
    environmentIds: filterProps.environmentIds,
    moodIds: filterProps.moodIds,
    colorIds: filterProps.colorIds,
    imageType: filterProps.imageType,
    description: debouncedDescription || description, // Pass search description
    // Only pass width/height if they're not the default values
    // This allows showing all images when using default dimensions
    width: (filterProps.width && filterProps.width !== "1024") ? parseInt(filterProps.width) : undefined,
    height: (filterProps.height && filterProps.height !== "1024") ? parseInt(filterProps.height) : undefined,
    searchQuery: debouncedDescription || description, // Also set searchQuery for compatibility
    projectId: filterProps.projectId, // Filter by project assignment
  };

  // Use private image library data hook with fixed search integration
  const {
    images,
    isLoading,
    isError,
    loadMore,
    isFetchingNextPage,
    hasNextPage,
    handleDeleteImage,
    searchTerm
  } = usePrivateImageLibraryData(filters);

  // ✅ SYNC FIX: Update selectedImage with fresh data after cache invalidation
  useEffect(() => {
    if (selectedImage && images.length > 0) {
      const freshImage = images.find(img => img.id === selectedImage.id);
      if (freshImage && JSON.stringify(freshImage) !== JSON.stringify(selectedImage)) {
        console.log('[PrivateImageGenerator] Syncing selectedImage with fresh cache data');
        setSelectedImage(freshImage); // Update with fresh data from cache
      }
    }
  }, [images, selectedImage, setSelectedImage]);

  // Auto-open image lightbox when requested via URL parameter
  useEffect(() => {
    if (autoOpenImageId && images.length > 0 && !selectedImage) {
      // Find the image in our loaded data
      const targetImage = images.find(img => img.id === autoOpenImageId);
      if (targetImage) {
        console.log('[PrivateImageLibrary] Auto-opening lightbox for image:', autoOpenImageId);
        setSelectedImage(targetImage);
        
        // Notify parent that auto-open is complete
        if (onAutoOpenComplete) {
          onAutoOpenComplete();
        }
      }
    }
  }, [autoOpenImageId, images, selectedImage, setSelectedImage, onAutoOpenComplete]);

  // Listen for custom events to open image lightbox
  useEffect(() => {
    const handleOpenImageEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const imageId = customEvent.detail?.imageId;
      
      console.log('[PrivateImageLibrary] Received open-image-lightbox event:', imageId);
      console.log('[PrivateImageLibrary] Currently loaded images:', {
        count: images.length,
        imageIds: images.map(img => img.id).slice(0, 5), // Show first 5 IDs
        isLoading: isLoading
      });
      
      if (imageId && images.length > 0) {
        const targetImage = images.find(img => img.id === imageId);
        if (targetImage) {
          console.log('[PrivateImageLibrary] Opening lightbox for image via event:', imageId);
          setSelectedImage(targetImage);
        } else {
          console.log('[PrivateImageLibrary] Image not found in loaded images:', imageId);
          console.log('[PrivateImageLibrary] Available image IDs:', images.map(img => img.id));
        }
      }
    };

    window.addEventListener('open-image-lightbox', handleOpenImageEvent);
    
    return () => {
      window.removeEventListener('open-image-lightbox', handleOpenImageEvent);
    };
  }, [images, setSelectedImage]);

  // Transform private images to match ImageGrid expectations
  const transformedImages = images.map(transformPrivateImageToLibraryItem);

  // Intersection observer for infinite scrolling
  const [loadMoreRef] = useIntersectionObserver({
    onIntersect: loadMore,
    enabled: !!hasNextPage && !isFetchingNextPage,
    rootMargin: '200px'
  });

  // Custom delete handler for ImageDetail
  const handleCustomDelete = async (image: PrivateImageGeneratorItem): Promise<void> => {
    console.log('[PrivateImageLibrary] handleCustomDelete called with image:', {
      imageId: image.id,
      userId: image.user_id,
      workbookId: image.workbook_id,
      filename: image.filename,
      imageUrl: image.image_url,
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log('[PrivateImageLibrary] Calling handleDeleteImage with ID:', image.id);
      await handleDeleteImage(image.id);
      console.log('[PrivateImageLibrary] handleDeleteImage completed successfully for ID:', image.id);
    } catch (error) {
      console.error('[PrivateImageLibrary] handleDeleteImage failed:', {
        imageId: image.id,
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error; // Re-throw to let the ImageDetail component handle the error display
    }
  };

  // Custom download handler for ImageDetail (basic implementation)
  const handleCustomDownload = async (image: PrivateImageGeneratorItem): Promise<void> => {
    try {
      // Create download link
      const link = document.createElement('a');
      link.href = image.image_url;
      link.download = `image-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Property accessors for ImageDetail
  const getImageUrl = (item: PrivateImageGeneratorItem): string => item.image_url;
  const getImageId = (item: PrivateImageGeneratorItem): string => item.id;
  const getImageDescription = (item: PrivateImageGeneratorItem): string | undefined => 
    item.generation_prompt || item.alt_text || undefined;
  const getUserId = (item: PrivateImageGeneratorItem): string | undefined => {
    console.log('[PrivateImageLibrary] getUserId called with item:', {
      itemUserId: item.user_id,
      itemId: item.id,
      timestamp: new Date().toISOString()
    });
    return item.user_id; // CHANGED: Return the actual user_id to help with debugging
  };
  const getFavoriteCount = (item: PrivateImageGeneratorItem): number | undefined => undefined; // No favorites for private images
  
  // Custom ownership check - private images always belong to the current user  
  const checkOwnership = (item: PrivateImageGeneratorItem): boolean => {
    console.log('[PrivateImageLibrary] checkOwnership called with item:', {
      itemUserId: item.user_id,
      itemId: item.id,
      result: true, // Always return true for private images
      timestamp: new Date().toISOString()
    });
    return true; // All private images belong to the current user
  };
  
  // Custom sidebar renderer for Private Image Generator
  const renderCustomSidebar = (image: PrivateImageGeneratorItem, context: any) => {
    return (
      <PrivateImageGeneratorSidebar
        image={image} // ✅ PASS: Original data format with correct id field for Private Image Generator
        // ✅ FORCE: Always set isOwner=true on Private Image Generator page
        isOwner={true}
        isDeleteLoading={context.isDeleteLoading}
        onDownload={context.onDownload}
        onDelete={context.onDelete}
        hasPrevious={context.hasPrevious}
        hasNext={context.hasNext}
        isLoadingMore={context.isLoadingMore}
        hasNextPage={context.hasNextPage}
        onPrevious={context.onPrevious}
        onNext={context.onNext}
        onLoadMore={context.onLoadMore}
      />
    );
  };

  // Enhanced empty state component matching the Research Generator's beautiful design
  const EmptyState = () => {
    const [isAccordionMode, setIsAccordionMode] = useState(() => {
      if (typeof window === 'undefined') return false;
      return window.innerWidth < 1024; // lg breakpoint
    });

    useEffect(() => {
      const mql = window.matchMedia('(max-width: 1023px)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        setIsAccordionMode(e.matches);
      };

      mql.addEventListener('change', handleChange);
      setIsAccordionMode(mql.matches);

      return () => {
        mql.removeEventListener('change', handleChange);
      };
    }, []);

    const ArrowIcon = isAccordionMode ? ArrowUp : ArrowRight;
    const actionText = isAccordionMode ? 'menu' : 'sidebar';

    return (
      <div className="space-y-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col items-center justify-center py-16 px-6 text-center"
        >
          {/* Beautiful icon with gradient background */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 flex items-center justify-center shadow-lg border border-blue-200/30 dark:border-blue-700/30"
          >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-500/10 dark:from-blue-400/20 dark:to-blue-400/20"></div>
            <Images className="w-10 h-10 text-blue-600 dark:text-blue-400 relative z-10" />
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3] 
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut" 
              }}
              className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400/20 to-blue-400/20"
            />
          </motion.div>

          {/* Title */}
          <motion.h3 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
          >
            {searchTerm ? 'No matching images found' : 'Your Images'}
          </motion.h3>

          {/* Description */}
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-gray-600 dark:text-gray-300 mb-8 max-w-lg leading-relaxed"
          >
            {searchTerm 
              ? `No images found matching "${searchTerm}". Try adjusting your search terms or generate new images with the AI generator.`
              : 'Make Van Gogh jealous with zero artistic talent required. Your visual masterpieces stay locked in your personal digital vault.'
            }
          </motion.p>

          {/* Call to action with arrow */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex items-center gap-3 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 border border-blue-200/40 dark:border-blue-700/40"
          >
            <span className="text-sm font-medium text-white">
              Use the Search & Create {actionText} to start
            </span>
            <motion.div
              animate={isAccordionMode ? { 
                y: [0, -3, 0],
              } : {
                x: [0, 3, 0],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut" 
              }}
            >
              <ArrowIcon className="w-4 h-4 text-white" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  if (isError) {
    return (
      <DashboardCard variant="purple" className="p-3 md:p-6">
        <ErrorState />
      </DashboardCard>
    );
  }

  return (
    <DashboardCard variant="purple" className="p-3 md:p-6">
      <div className="space-y-4">
        {/* Header Section (matching Project Image Library style) */}
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <Images className="text-blue-500 dark:text-blue-400" size={18} />
            <h3 className="font-medium text-base">
              {selectedProject ? selectedProject.title : 'Your Images'}
            </h3>
            <GlassPill 
              type={images.length.toString()} 
              variant="blue" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          </div>
        </div>
        
        {/* Search feedback */}
        {(() => {
          const activeFilters = getActiveFilters();
          return activeFilters.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex flex-wrap items-center gap-1">
              <span>Showing results for:</span>
              {activeFilters.map((filter, index) => (
                <React.Fragment key={index}>
                  <span className="font-medium">{filter}</span>
                  {index < activeFilters.length - 1 && <span>•</span>}
                </React.Fragment>
              ))}
            </p>
          );
        })()}

        {/* Content */}
        {isLoading && images.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="aspect-square bg-gray-200 dark:bg-gray-700 animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : images.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <ImageGrid
              images={transformedImages}
              onDeleteImage={handleDeleteImage}
              onSelectImage={(image) => {
                // Find the original private image item
                const originalImage = images.find(img => img.id === image.id);
                if (originalImage) {
                  setSelectedImage(originalImage);
                }
              }}
              isLoading={isFetchingNextPage}
              contentType="private_image" // NEW: Use private_image content type
              hideStorageButton={true}
            />

            {/* Load More Indicator */}
            {(hasNextPage || isFetchingNextPage) && (
              <LoadMoreIndicator 
                isFetchingNextPage={isFetchingNextPage}
                loadMoreRef={loadMoreRef}
              />
            )}
          </>
        )}
      </div>

      {/* Image Detail Modal */}
      <ImageDetail<PrivateImageGeneratorItem>
        image={selectedImage}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        allImages={images}
        onNavigate={(image) => {
          setSelectedImage(image);
        }}
        loadMore={loadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        
        // Property accessors for PrivateImageGeneratorItem
        getImageUrl={getImageUrl}
        getImageId={getImageId}
        getImageDescription={getImageDescription}
        getUserId={getUserId}
        getFavoriteCount={getFavoriteCount}
        
        // Custom handlers
        onDelete={handleCustomDelete}
        onDownload={handleCustomDownload}
        checkOwnership={checkOwnership}
        
        // Custom sidebar that includes project management
        renderSidebar={renderCustomSidebar}
        
        // Display options - disable favorites for private images
        showFavoriteButton={false}
        showDescription={true}
        dropdownLabel="Private Image"
      />
    </DashboardCard>
  );
}; 